/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-16 06:24:30
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { showSABToast } from 'src/integrations/shopify-app-bridge/toast';
import { processRes } from 'src/helpers/actions-helper';
import { downloadFile } from 'src/helpers';
import apiService from 'src/services/api-service';
import { NUMBER_ITEMS_ON_LIST, NUMBER_DAYS_ON_TOTAL } from 'src/constants/analytics';
import {
  GET_ANALYTICS_SETTINGS_REQUEST,
  GET_ANALYTICS_SETTINGS_SUCCESS,
  GET_ANALYTICS_SETTINGS_FAILURE,
  UPDATE_ANALYTICS_SETTINGS_REQUEST,
  UPDATE_ANALYTICS_SETTINGS_SUCCESS,
  UPDATE_ANALYTICS_SETTINGS_FAILURE,
  GET_TOTAL_REQUEST,
  GET_TOTAL_SUCCESS,
  GET_TOTAL_FAILURE,
  GET_TOTAL_ALL_SALES_REQUEST,
  GET_TOTAL_ALL_SALES_SUCCESS,
  GET_TOTAL_ALL_SALES_FAILURE,
  GET_TOP_FILTER_OPTION_VALUES_REQUEST,
  GET_TOP_FILTER_OPTION_VALUES_SUCCESS,
  GET_TOP_FILTER_OPTION_VALUES_FAILURE,
  GET_TOP_FILTER_OPTION_VALUE_COMBINATIONS_REQUEST,
  GET_TOP_FILTER_OPTION_VALUE_COMBINATIONS_SUCCESS,
  GET_TOP_FILTER_OPTION_VALUE_COMBINATIONS_FAILURE,
  GET_TOP_COLLECTIONS_TOTAL_FILTER_CLICKS_REQUEST,
  GET_TOP_COLLECTIONS_TOTAL_FILTER_CLICKS_SUCCESS,
  GET_TOP_COLLECTIONS_TOTAL_FILTER_CLICKS_FAILURE,
  GET_COLLECTION_TOTAL_DETAILS_BY_TIME_FILTER_CLICKS_REQUEST,
  GET_COLLECTION_TOTAL_DETAILS_BY_TIME_FILTER_CLICKS_SUCCESS,
  GET_COLLECTION_TOTAL_DETAILS_BY_TIME_FILTER_CLICKS_FAILURE,
  GET_TOP_FILTER_OPTION_VALUES_ON_SEARCH_PAGE_REQUEST,
  GET_TOP_FILTER_OPTION_VALUES_ON_SEARCH_PAGE_SUCCESS,
  GET_TOP_FILTER_OPTION_VALUES_ON_SEARCH_PAGE_FAILURE,
  GET_TOP_SEARCH_TERMS_REQUEST,
  GET_TOP_SEARCH_TERMS_SUCCESS,
  GET_TOP_SEARCH_TERMS_FAILURE,
  GET_TOTAL_SEARCHES_REQUEST,
  GET_TOTAL_SEARCHES_SUCCESS,
  GET_TOTAL_SEARCHES_FAILURE,
  GET_TOTAL_FILTER_CLICKS_ON_SEARCH_REQUEST,
  GET_TOTAL_FILTER_CLICKS_ON_SEARCH_SUCCESS,
  GET_TOTAL_FILTER_CLICKS_ON_SEARCH_FAILURE,
  GET_TOP_COLLECTIONS_TOTAL_SALES_REQUEST,
  GET_TOP_COLLECTIONS_TOTAL_SALES_SUCCESS,
  GET_TOP_COLLECTIONS_TOTAL_SALES_FAILURE,
  GET_TOTAL_SALES_REQUEST,
  GET_TOTAL_SALES_SUCCESS,
  GET_TOTAL_SALES_FAILURE,
  GET_TOTAL_SALES_ON_SEARCH_REQUEST,
  GET_TOTAL_SALES_ON_SEARCH_SUCCESS,
  GET_TOTAL_SALES_ON_SEARCH_FAILURE,
  EXPORT_DATA_REQUEST,
  EXPORT_DATA_SUCCESS,
  EXPORT_DATA_FAILURE,
  CHANGE_VALUE
} from 'stores/flow-type/analytics.type';
import { getStoreDate } from 'src/helpers/analytics-helpers';

/**
 * call to get analytics settings api
 * @return {object} The RSAA object for API middleware
 */
export const getAnalyticsSettings = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.analytics.get-analytics-settings', {}, [
    GET_ANALYTICS_SETTINGS_REQUEST,
    {
      type: GET_ANALYTICS_SETTINGS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_ANALYTICS_SETTINGS_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getAnalyticsSettings'),
          i18n
        )
    }
  ]);
};

/**
 * call to update analytics settings api
 * @return {object} The RSAA object for API middleware
 */
export const updateAnalyticsSettings = (body, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.analytics.update-analytics-settings', { body }, [
    UPDATE_ANALYTICS_SETTINGS_REQUEST,
    {
      type: UPDATE_ANALYTICS_SETTINGS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'info',
          i18n.translate('AdminHTML.Analytics.Actions.updateAnalyticsSettings_Success'),
          i18n
        );
        return { data };
      }
    },
    {
      type: UPDATE_ANALYTICS_SETTINGS_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.updateAnalyticsSettings_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to get total
 * @return {object} The RSAA object for API middleware
 */
export const getTotal = (recentDays = NUMBER_DAYS_ON_TOTAL, queryExecutionId, i18n: I18n) => {
  const queries = _.isEmpty(queryExecutionId) ? { recentDays } : { queryExecutionId };
  return apiService.sendRequest('admin-v3.analytics.get-total', { queries }, [
    GET_TOTAL_REQUEST,
    {
      type: GET_TOTAL_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_TOTAL_FAILURE,
      payload: async (action, state, res) =>
        await processRes(res, 'error', i18n.translate('AdminHTML.Analytics.Actions.getTotal'), i18n)
    }
  ]);
};

/**
 * call to get total all sales
 * @return {object} The RSAA object for API middleware
 */
export const getTotalAllSales = (
  recentDays = NUMBER_DAYS_ON_TOTAL,
  queryExecutionId,
  i18n: I18n
) => {
  const queries = _.isEmpty(queryExecutionId) ? { recentDays } : { queryExecutionId };
  return apiService.sendRequest('admin-v3.analytics.get-total-all-sales', { queries }, [
    GET_TOTAL_ALL_SALES_REQUEST,
    {
      type: GET_TOTAL_ALL_SALES_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_TOTAL_ALL_SALES_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getTotalAllSales'),
          i18n
        )
    }
  ]);
};
/**
 * call to get top of filter option values
 * @return {object} The RSAA object for API middleware
 */
export const getTopFilterOptionValues = (
  from,
  to,
  compare,
  collectionId,
  queryExecutionId,
  i18n: I18n
) => {
  let queries = {};

  if (_.isEmpty(queryExecutionId)) {
    const top = NUMBER_ITEMS_ON_LIST;
    const today = getStoreDate(new Date());
    const fromDate = from ? from.toISOString() : today.toISOString();
    const toDate = to ? to.toISOString() : today.toISOString();

    queries = { fromDate, toDate, compare, collectionId, top };
  } else {
    queries = { compare, queryExecutionId };
  }

  return apiService.sendRequest('admin-v3.analytics.get-top-filter-option-values', { queries }, [
    GET_TOP_FILTER_OPTION_VALUES_REQUEST,
    {
      type: GET_TOP_FILTER_OPTION_VALUES_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_TOP_FILTER_OPTION_VALUES_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getTopFilterOptionValues'),
          i18n
        )
    }
  ]);
};

/**
 * call to get top of filter option value combinations
 * @return {object} The RSAA object for API middleware
 */
export const getTopFilterOptionValueCombinations = (
  from,
  to,
  compare,
  collectionId,
  queryExecutionId,
  i18n: I18n
) => {
  let queries = {};
  if (_.isEmpty(queryExecutionId)) {
    const top = NUMBER_ITEMS_ON_LIST;
    const today = getStoreDate(new Date());
    const fromDate = from ? from.toISOString() : today.toISOString();
    const toDate = to ? to.toISOString() : today.toISOString();

    queries = { fromDate, toDate, compare, collectionId, top };
  } else {
    queries = { compare, queryExecutionId };
  }

  return apiService.sendRequest(
    'admin-v3.analytics.get-top-filter-option-value-combinations',
    { queries },
    [
      GET_TOP_FILTER_OPTION_VALUE_COMBINATIONS_REQUEST,
      {
        type: GET_TOP_FILTER_OPTION_VALUE_COMBINATIONS_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_TOP_FILTER_OPTION_VALUE_COMBINATIONS_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Analytics.Actions.getTopFilterOptionValueCombinations'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to change value in store
 * @return {object} action object
 */
export const changeValue = (name, value) => {
  return { type: CHANGE_VALUE, payload: { name, value } };
};

/**
 * call to get top of filter option value combinations
 * @return {object} The RSAA object for API middleware
 */
export const getTopCollectionsTotalFilterClicks = (
  from,
  to,
  compare,
  queryExecutionId,
  i18n: I18n
) => {
  let queries = {};

  if (_.isEmpty(queryExecutionId)) {
    const top = 20;
    const today = getStoreDate(new Date());
    const fromDate = from ? from.toISOString() : today.toISOString();
    const toDate = to ? to.toISOString() : today.toISOString();

    queries = { fromDate, toDate, compare, top };
  } else {
    queries = { compare, queryExecutionId };
  }

  return apiService.sendRequest(
    'admin-v3.analytics.get-top-collections-total-filter-clicks',
    { queries },
    [
      GET_TOP_COLLECTIONS_TOTAL_FILTER_CLICKS_REQUEST,
      {
        type: GET_TOP_COLLECTIONS_TOTAL_FILTER_CLICKS_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_TOP_COLLECTIONS_TOTAL_FILTER_CLICKS_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Analytics.Actions.getTopCollectionsTotalFilterClicks'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to get top of filter option value combinations
 * @return {object} The RSAA object for API middleware
 */
export const getCollectionTotalDetailsByTimeFilterClicks = (
  from,
  to,
  compare,
  collectionId,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  let queries = {};

  const top = 20;
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = {
    fromDate,
    toDate,
    compare,
    collectionId,
    top,
    queryExecutionId,
    comparedExecutionId
  };

  return apiService.sendRequest('admin-v3.analytics.get-total-filter-clicks', { queries }, [
    GET_COLLECTION_TOTAL_DETAILS_BY_TIME_FILTER_CLICKS_REQUEST,
    {
      type: GET_COLLECTION_TOTAL_DETAILS_BY_TIME_FILTER_CLICKS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_COLLECTION_TOTAL_DETAILS_BY_TIME_FILTER_CLICKS_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getCollectionTotalDetailsByTimeFilterClicks'),
          i18n
        )
    }
  ]);
};

/**
 * call to get top of filter option values on search page
 * @return {object} The RSAA object for API middleware
 */
export const getTopFilterOptionValuesOnSearchPage = (
  from,
  to,
  compare,
  queryExecutionId,
  i18n: I18n
) => {
  let queries = {};
  if (_.isEmpty(queryExecutionId)) {
    const top = NUMBER_ITEMS_ON_LIST;
    const today = getStoreDate(new Date());
    const fromDate = from ? from.toISOString() : today.toISOString();
    const toDate = to ? to.toISOString() : today.toISOString();
    const searchPages = true;
    queries = { fromDate, toDate, compare, top, searchPages };
  } else {
    queries = { compare, queryExecutionId };
  }

  return apiService.sendRequest('admin-v3.analytics.get-top-filter-option-values', { queries }, [
    GET_TOP_FILTER_OPTION_VALUES_ON_SEARCH_PAGE_REQUEST,
    {
      type: GET_TOP_FILTER_OPTION_VALUES_ON_SEARCH_PAGE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_TOP_FILTER_OPTION_VALUES_ON_SEARCH_PAGE_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getTopFilterOptionValuesOnSearchPage'),
          i18n
        )
    }
  ]);
};

/**
 * call to get search term
 * @return {object} The RSAA object for API middleware
 */
export const getTopSearchTerms = (from, to, compare, noResult, queryExecutionId, i18n: I18n) => {
  let queries = {};
  if (_.isEmpty(queryExecutionId)) {
    const top = NUMBER_ITEMS_ON_LIST;
    const today = getStoreDate(new Date());
    const fromDate = from ? from.toISOString() : today.toISOString();
    const toDate = to ? to.toISOString() : today.toISOString();

    queries = { fromDate, toDate, compare, noResult, top };
  } else {
    queries = { compare, queryExecutionId };
  }

  return apiService.sendRequest('admin-v3.analytics.get-top-search-terms', { queries }, [
    {
      type: GET_TOP_SEARCH_TERMS_REQUEST,
      payload: (action, state, res) => {
        return { noResult };
      }
    },
    {
      type: GET_TOP_SEARCH_TERMS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data, noResult };
      }
    },
    {
      type: GET_TOP_SEARCH_TERMS_FAILURE,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getTopSearchTerms'),
          i18n
        );
        return { ...data, noResult };
      }
    }
  ]);
};

/**
 * call to get total searches
 * @return {object} The RSAA object for API middleware
 */
export const getTotalSearches = (
  from,
  to,
  compare,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  let queries = {};
  const top = NUMBER_ITEMS_ON_LIST;
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = { fromDate, toDate, compare, top, queryExecutionId, comparedExecutionId };

  return apiService.sendRequest('admin-v3.analytics.get-total-searches', { queries }, [
    GET_TOTAL_SEARCHES_REQUEST,
    {
      type: GET_TOTAL_SEARCHES_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_TOTAL_SEARCHES_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getTotalSearches'),
          i18n
        )
    }
  ]);
};

/**
 * call to get total filter clicks on search
 * @return {object} The RSAA object for API middleware
 */
export const getTotalFilterClicksOnSearch = (
  from,
  to,
  compare,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  let queries = {};
  const top = NUMBER_ITEMS_ON_LIST;
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();
  const searchPages = true;

  queries = {
    fromDate,
    toDate,
    compare,
    top,
    searchPages,
    queryExecutionId,
    comparedExecutionId
  };

  return apiService.sendRequest('admin-v3.analytics.get-total-filter-clicks', { queries }, [
    GET_TOTAL_FILTER_CLICKS_ON_SEARCH_REQUEST,
    {
      type: GET_TOTAL_FILTER_CLICKS_ON_SEARCH_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_TOTAL_FILTER_CLICKS_ON_SEARCH_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getTotalFilterClicksOnSearch'),
          i18n
        )
    }
  ]);
};

/**
 * call to get top of filter option value combinations
 * @return {object} The RSAA object for API middleware
 */
export const getTopCollectionsTotalSales = (from, to, compare, queryExecutionId, i18n: I18n) => {
  let queries = {};

  if (_.isEmpty(queryExecutionId)) {
    const top = 20;
    const today = getStoreDate(new Date());
    const fromDate = from ? from.toISOString() : today.toISOString();
    const toDate = to ? to.toISOString() : today.toISOString();

    queries = { fromDate, toDate, compare, top };
  } else {
    queries = { compare, queryExecutionId };
  }

  return apiService.sendRequest('admin-v3.analytics.get-top-collections-total-sales', { queries }, [
    GET_TOP_COLLECTIONS_TOTAL_SALES_REQUEST,
    {
      type: GET_TOP_COLLECTIONS_TOTAL_SALES_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_TOP_COLLECTIONS_TOTAL_SALES_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getTopCollectionsTotalSales'),
          i18n
        )
    }
  ]);
};

/**
 * call to get total sales
 * @return {object} The RSAA object for API middleware
 */
export const getTotalSales = (
  from,
  to,
  compare,
  collectionId,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  const top = 20;
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  let queries = {
    fromDate,
    toDate,
    compare,
    collectionId,
    top,
    queryExecutionId,
    comparedExecutionId
  };

  return apiService.sendRequest('admin-v3.analytics.get-total-sales', { queries }, [
    GET_TOTAL_SALES_REQUEST,
    {
      type: GET_TOTAL_SALES_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_TOTAL_SALES_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getTotalSales'),
          i18n
        )
    }
  ]);
};

/**
 * call to get total sales
 * @return {object} The RSAA object for API middleware
 */
export const getTotalSalesOnSearch = (
  from,
  to,
  compare,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  const top = 20;
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();
  const searchPages = true;

  let queries = {
    fromDate,
    toDate,
    compare,
    top,
    queryExecutionId,
    comparedExecutionId,
    searchPages
  };

  return apiService.sendRequest('admin-v3.analytics.get-total-sales', { queries }, [
    GET_TOTAL_SALES_ON_SEARCH_REQUEST,
    {
      type: GET_TOTAL_SALES_ON_SEARCH_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_TOTAL_SALES_ON_SEARCH_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getTotalSalesOnSearch'),
          i18n
        )
    }
  ]);
};

/**
 * call to export data
 * @return {object} The RSAA object for API middleware
 */
export const exportData = (
  isCollection,
  collectionId,
  collectionLabel,
  from,
  to,
  fileFormat,
  i18n: I18n
) => {
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  let queries = {
    fromDate,
    toDate,
    searchPages: !isCollection,
    collectionId,
    fileFormat
  };

  return apiService.sendRequest('admin-v3.analytics.export-data', { queries }, [
    EXPORT_DATA_REQUEST,
    {
      type: EXPORT_DATA_SUCCESS,
      payload: async (action, state, res) => {
        // Convert response data into blob and download
        const base64String = await res.text();
        const blob = Buffer.from(base64String, 'base64');
        const formatDates = `${fromDate.split('T')[0]}_${toDate.split('T')[0]}`;
        const collectionName = queries.searchPages ? `_` : `_${collectionLabel}_`;
        const fileName = `${
          queries.searchPages ? 'search-analytics' : 'collection-analytics'
        }${collectionName}${formatDates}.${
          queries.fileFormat && queries.fileFormat[0] === 'csv' ? 'zip' : 'xlsx'
        }`;
        showSABToast(i18n.translate('AdminHTML.Analytics.ExportModal.exportedFiles'));
        downloadFile(blob, fileName);
      }
    },
    {
      type: EXPORT_DATA_FAILURE,
      payload: async (action, state, res) =>
        showSABToast(i18n.translate('AdminHTML.Analytics.Actions.exportData'))
    }
  ]);
};
